import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { SignOut, Trash, Warning, ArrowRight, SpinnerGap } from "@phosphor-icons/react";

export function DangerZoneSection({
  user, onLogout, deletionStatus, onCancelDeletion, onOpenDeleteDialog,
  deleteConfirm, onDeleteConfirmChange, confirmEmail, onConfirmEmailChange,
  onDeleteAccount, deletingAccount, onExportData,
}) {
  return (
    <>
      <section className="mt-6">
        <h2 className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-red-400/60 mb-3">Danger Zone</h2>
        <div className="bg-[var(--bg-card)] border border-red-500/10 rounded-xl p-5 space-y-4">
          <div>
            <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-1">Account Email</label>
            <p className="text-sm text-[var(--text-secondary)]" data-testid="account-email">{user?.email}</p>
          </div>

          <div className="flex items-center justify-between py-3 border-y border-[var(--border)]">
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">Download My Data</p>
              <p className="text-xs text-[var(--text-muted)]">Export all your content, profiles, files, and account data as JSON.</p>
            </div>
            <Button
              onClick={onExportData}
              variant="outline"
              size="sm"
              className="border-[var(--border-strong)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] text-xs"
              data-testid="download-data-btn"
            >
              <ArrowRight size={14} className="mr-1.5 rotate-90" /> Export JSON
            </Button>
          </div>

          {deletionStatus && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="deletion-pending-banner">
              <Warning size={18} className="text-red-400 shrink-0" weight="fill" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-400">Account deletion scheduled</p>
                <p className="text-xs text-[var(--text-muted)]">
                  All data will be permanently deleted on {new Date(deletionStatus.delete_at).toLocaleString()}.
                  You can cancel before then.
                </p>
              </div>
              <Button onClick={onCancelDeletion} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="cancel-deletion-btn">
                Cancel Deletion
              </Button>
            </div>
          )}

          <div className="flex items-center gap-3 pt-3 border-t border-[var(--border)]">
            <Button onClick={onLogout} variant="outline" className="border-[var(--border-strong)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]" data-testid="settings-logout-button">
              <SignOut size={16} className="mr-2" />Logout
            </Button>
            {!deletionStatus && (
              <Button onClick={onOpenDeleteDialog} variant="outline" className="border-red-500/20 text-red-400 hover:bg-red-500/10" data-testid="delete-account-button">
                <Trash size={16} className="mr-2" />Delete My Account
              </Button>
            )}
          </div>
        </div>
      </section>

      <Dialog open={deleteConfirm} onOpenChange={(v) => { onDeleteConfirmChange(v); if (!v) onConfirmEmailChange(""); }}>
        <DialogContent className="bg-[var(--bg-raised)] border-[var(--border-strong)] text-[var(--text-primary)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--text-primary)] flex items-center gap-2">
              <Warning size={20} className="text-red-400" weight="fill" />Delete My Account
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[var(--text-secondary)] text-sm">
              This will schedule your account for <strong>permanent deletion in 72 hours</strong>. During the grace period, you can cancel from this page. After 72 hours, all data is irreversibly removed.
            </p>
            <p className="text-xs text-[var(--text-muted)]">
              Deleted: all content, brand profiles, resource bank files, templates, API keys, payment records, and team memberships.
              <a href="/legal/data-retention" target="_blank" className="text-[#FF4400] hover:underline ml-1">See full policy</a>
            </p>
            <div>
              <label className="text-xs text-[var(--text-muted)] mb-1 block">Type your email to confirm:</label>
              <Input
                value={confirmEmail}
                onChange={(e) => onConfirmEmailChange(e.target.value)}
                placeholder={user?.email || "your@email.com"}
                className="bg-[var(--bg-base)] border-[var(--border)] text-[var(--text-primary)]"
                data-testid="delete-account-email-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { onDeleteConfirmChange(false); onConfirmEmailChange(""); }} className="border-[var(--border-strong)] text-[var(--text-secondary)]" data-testid="delete-account-cancel">Cancel</Button>
            <Button
              onClick={onDeleteAccount}
              disabled={!confirmEmail.trim() || confirmEmail.toLowerCase() !== user?.email?.toLowerCase() || deletingAccount}
              className="bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
              data-testid="delete-account-confirm"
            >
              {deletingAccount ? <SpinnerGap size={16} className="animate-spin" /> : "Schedule Deletion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
